import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { CompositeDecorator, Editor, EditorState, EntityInstance, RichUtils } from 'draft-js';
import { makeStyles } from '@material-ui/core/styles';
import { WrappedFieldProps } from 'redux-form';
import 'draft-js/dist/Draft.css';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import i18n from '../../../i18n';

function findLinkEntities(contentBlock: any, callback: any, contentState: any) {
    contentBlock.findEntityRanges((character: any) => {
        const entityKey = character.getEntity();
        return entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK';
    }, callback);
}

const Link = (props: any) => {
    const { url } = props.contentState.getEntity(props.entityKey).getData();
    return (
        <a href={url} target="_blank" style={{ textDecoration: 'underline' }}>
            {props.children}
        </a>
    );
};

const decorator = new CompositeDecorator([
    {
        strategy: findLinkEntities,
        component: Link,
    },
]);

const options = {
    entityStyleFn: (entity: EntityInstance) => {
        const entityType = entity.get('type').toLowerCase();
        if (entityType === 'link') {
            const data = entity.getData();
            return {
                element: 'a',
                attributes: {
                    href: data.url,
                    target: '_blank',
                },
            };
        }
    },
};

export interface RichTextControlProps {
    onChange: (val: string) => void;
    // Optional:
    name?: string;
    value?: string;
    label?: string;
}

interface BlockType {
    label: string;
    style: string;
}

interface RichControlsProps {
    editorState: EditorState;
    onToggle: (type: string) => void;
}

const BLOCK_TYPES: Array<BlockType> = [
    { label: 'H1', style: 'header-one' },
    { label: 'H2', style: 'header-two' },
    { label: 'H3', style: 'header-three' },
    // { label: 'H4', style: 'header-four' },
    // { label: 'H5', style: 'header-five' },
    // { label: 'H6', style: 'header-six' },
    // { label: 'Blockquote', style: 'blockquote' },
    { label: 'UL', style: 'unordered-list-item' },
    { label: 'OL', style: 'ordered-list-item' },
    { label: 'Link', style: 'LINK' },
];

const useInputStyles = makeStyles(theme => ({
    root: {
        borderRadius: theme.spacing(0.75),
        background: 'rgba(15, 24, 37, 0.05)',
        padding: '16px 16px 10px',
    },
    editor: {
        borderTop: '1px solid #ddd',
        cursor: 'text',
        fontSize: 16,
        paddingTop: 10,
    },
}));

export const RichTextControl: FC<RichTextControlProps> = props => {
    const className = useInputStyles();
    const { onChange, value, label, ...rest } = props;

    const { t } = i18n.useTranslation();

    const editor = useRef<DraftEditor>();
    const [editorState, setEditorState] = useState(
        EditorState.createWithContent(stateFromHTML(value || ''), decorator),
    );
    const handleChange = useCallback(
        (newEditorState: EditorState) => {
            setEditorState(newEditorState);
            onChange(stateToHTML(newEditorState.getCurrentContent(), options));
        },
        [onChange, setEditorState],
    );

    // Костыль
    const [firstLoad, setFirstLoad] = useState(true);
    useEffect(() => {
        if (firstLoad && value) {
            setEditorState(EditorState.createWithContent(stateFromHTML(value || ''), decorator));
            setFirstLoad(false);
        }
    }, [firstLoad, value]);
    // Конец костыля

    const handleKeyCommand = useCallback(
        (command: string) => {
            const newState = RichUtils.handleKeyCommand(editorState, command);
            if (newState) {
                handleChange(newState);
                return true;
            }
            return false;
        },
        [editorState],
    );

    const onTab = useCallback(
        e => {
            const maxDepth = 4;
            handleChange(RichUtils.onTab(e, editorState, maxDepth));
        },
        [editorState],
    );

    const onFocus = useCallback(() => {
        editor.current.focus();
    }, [editor]);

    const toggleBlockType = useCallback(
        (blockType: string) => {
            if (blockType === 'LINK') {
                onAddLink();
            } else {
                handleChange(RichUtils.toggleBlockType(editorState, blockType));
            }
        },
        [editorState],
    );

    const toggleInlineStyle = useCallback(
        (inlineStyle: string) => {
            handleChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
        },
        [editorState],
    );

    const onAddLink = () => {
        const selection = editorState.getSelection();

        const link = window.prompt(t('common:pasteLink'));
        if (!link) {
            handleChange(RichUtils.toggleLink(editorState, selection, null));
            return 'handled';
        }
        const content = editorState.getCurrentContent();
        const contentWithEntity = content.createEntity('LINK', 'MUTABLE', {
            url: link,
        });
        const newEditorState = EditorState.push(editorState, contentWithEntity, 'create-entity');
        const entityKey = contentWithEntity.getLastCreatedEntityKey();
        handleChange(RichUtils.toggleLink(newEditorState, selection, entityKey));
        return 'handled';
    };

    return (
        <div className={className.root}>
            <BlockStyleControls editorState={editorState} onToggle={toggleBlockType} />
            <InlineStyleControls editorState={editorState} onToggle={toggleInlineStyle} />
            <div className={className.editor} onClick={onFocus}>
                <Editor
                    editorState={editorState}
                    // @ts-ignore
                    handleKeyCommand={handleKeyCommand}
                    onChange={handleChange}
                    onTab={onTab}
                    placeholder={label}
                    ref={editor}
                    spellCheck={true}
                />
            </div>
        </div>
    );
};

const useControlStyles = makeStyles({
    controls: {
        fontFamily: "'Helvetica', sans-serif",
        fontSize: 14,
        marginBottom: 5,
        userSelect: 'none',
    },
});

const BlockStyleControls = (props: RichControlsProps) => {
    const { editorState } = props;
    const className = useControlStyles();
    const selection = editorState.getSelection();
    const blockType = editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType();

    return (
        <div className={className.controls}>
            {BLOCK_TYPES.map(type => (
                <StyleButton
                    key={type.label}
                    active={type.style === blockType}
                    label={type.label}
                    onToggle={props.onToggle}
                    style={type.style}
                />
            ))}
        </div>
    );
};

const INLINE_STYLES = [
    { label: 'Bold', style: 'BOLD' },
    { label: 'Italic', style: 'ITALIC' },
    // { label: 'Underline', style: 'UNDERLINE' },
    // { label: 'Monospace', style: 'CODE' },
];

interface StyleButtonProps {
    active: boolean;
    label: string;
    onToggle: (style: string) => void;
    style: string;
}

const useStylesStyleButton = makeStyles({
    button: {
        color: (params: { active: boolean }) => (!params.active ? '#999' : '#5890ff'),
        cursor: 'pointer',
        marginRight: 16,
        padding: '2px 0',
        display: 'inline-block',
    },
});

const StyleButton = (props: StyleButtonProps) => {
    const { active, style, label, onToggle } = props;
    const className = useStylesStyleButton({ active });

    const handleToggle = useCallback(
        e => {
            e.preventDefault();
            onToggle(style);
        },
        [onToggle, style],
    );

    return (
        <span className={className.button} onMouseDown={handleToggle}>
            {label}
        </span>
    );
};

const InlineStyleControls = (props: RichControlsProps) => {
    const currentStyle = props.editorState.getCurrentInlineStyle();
    const className = useControlStyles();
    return (
        <div className={className.controls}>
            {INLINE_STYLES.map(type => (
                <StyleButton
                    key={type.label}
                    active={currentStyle.has(type.style)}
                    label={type.label}
                    onToggle={props.onToggle}
                    style={type.style}
                />
            ))}
        </div>
    );
};

export type RichTextControlFieldProps = RichTextControlProps & WrappedFieldProps;

export const RichTextControlField = (props: RichTextControlFieldProps) => {
    const {
        input,
        label,
        meta: { touched, invalid, error },
        ...rest
    } = props;

    return <RichTextControl label={label} {...input} {...rest} />;
};
